import { Box, Flex, Image } from '@chakra-ui/react';
import styled from '@emotion/styled';
import React, { useCallback } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { FilterIcon, Heading } from 'ui-components';
import { SavedList } from '~/clientApi/savedLists.model';
import Spinner from '~/components/Spinner';
import { TalentRosterEmptyResults } from '~/components/TalentRosterEmptyResults';
import { useGetSharedSavedListCreators } from '~/hooks/api/useSavedLists';
import { StackCreatorSkeleton } from '~/screens/WhalarModeListDetailScreen/components/CreatorSkeleton';
import { ListView } from '~/screens/WhalarModeListDetailScreen/components/ListView';
import { StackViewCreator } from '~/screens/WhalarModeListDetailScreen/components/StackViewCreator';
import { IStackViewCreatorProps } from '~/screens/WhalarModeScreen/components/StackView/StackViewCreator';
import { ToggleView } from '~/screens/WhalarModeScreen/components/ToggleView';
import {
  VIEW_TYPE,
  ViewType,
} from '~/screens/WhalarModeScreen/hooks/useActiveView';
import { useFilterSortFields } from '~/screens/WhalarModeScreen/hooks/useFilterSortFields';
import { IShareLinkMapped } from '~/shareLinkApi/shareLink.model';
import { ICreator } from '~/types';

import { useGroupFilters } from '../hooks/useGroupFilters';
import { useMakeAdvancedFiltersToString } from '../hooks/useMakeAdvancedFiltersToString';
import { useAdvancedFiltersStore } from '../store/useAdvancedFilterStore';
import { useCreatorsFiltersStore } from '../store/useCreatorsFiltersStore';
import { AppliedAdvancedFilters } from './AppliedAdvancedFilters';
import { ClearAllFiltersButton } from './ClearAllFiltersButton';
import { HeaderButton } from './HeaderButton';
import { SortCreatorsDropDown } from './SortCreatorsDropDown/SortCreatorsDropDown';
import { FiltersDrawer } from './TalentFilters/FiltersDrawer';
import { TalentFilters } from './TalentFilters/TalentFilters';

interface SharedListContentProps {
  activeView: ViewType;
  setActiveView?: (view: ViewType) => void;
  list: SavedList;
  link?: IShareLinkMapped;
  onNoteOpen: (creator: ICreator) => void;
  sharedToken: string;
}

const ListContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  flex-wrap: wrap;
  & > * {
    width: 100%;
  }
`;

export function SharedListContent({
  activeView,
  setActiveView,
  list,
  link,
  onNoteOpen,
  sharedToken,
}: SharedListContentProps) {
  const {
    sortField,
    sortDirection,
    setSortingOrder,
    toggleOrder,
    isFieldSorted,
  } = useFilterSortFields({});

  const {
    openCreatorsFiltersDrawer,
    appliedFilters: appliedCreatorFilters,
    clearAllFilters,
  } = useCreatorsFiltersStore();

  const {
    setIsDrawerOpen: openFiltersDrawer,
    appliedFilters,
    clearFilters,
  } = useAdvancedFiltersStore();

  const appliedFiltersGrouped = useGroupFilters(appliedFilters);
  const advancedFiltersAsString = useMakeAdvancedFiltersToString(
    appliedFiltersGrouped,
  );

  const allFilters = [...advancedFiltersAsString, ...appliedCreatorFilters];

  const name = list?.name || list?.agencyName || '';
  const isAudienceFiltersEnabled = link
    ? !link.options?.hideAudienceFilters
    : list?.enabledControls.includes('audience-filters') || false;

  const handleAudienceClick = useCallback(() => {
    openFiltersDrawer(true);
  }, [openFiltersDrawer]);

  const handleTalentClick = useCallback(() => {
    openCreatorsFiltersDrawer();
  }, [openCreatorsFiltersDrawer]);

  const handleClearFilters = useCallback(() => {
    clearAllFilters();
    clearFilters();
  }, [clearAllFilters, clearFilters]);

  const {
    data,
    fetchNextPage,
    isInitialFetch,
    isFetchingNextPage,
    hasNextPage,
    isLoading,
  } = useGetSharedSavedListCreators({
    id: list?.id,
    sharedToken,
    filters: allFilters,
    sortField,
    sortDirection,
  });

  const creators = data?.creators || [];

  const totalItems = data?.totalItems;

  const handleFetchNextPage = useCallback(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage]);

  return (
    <>
      <Box>
        <Flex
          width="100%"
          justifyContent="space-between"
          marginBottom={4}
          alignItems="center"
          flexWrap="wrap"
          flexDirection={{ base: 'column', md: 'row' }}
          gap={4}
        >
          <Flex gap={2} alignItems="center">
            {list?.agencyLogo && (
              <Image src={list.agencyLogo} height={12} alt={name} />
            )}
            <Heading as="h1">{name}</Heading>
          </Flex>
          <Flex justifyContent="flex-end" alignItems="center" gap={2}>
            {allFilters.length > 0 ? (
              <ClearAllFiltersButton onClear={handleClearFilters} />
            ) : null}
            <SortCreatorsDropDown
              sortField={sortField}
              sortDirection={sortDirection}
              setSortingOrder={setSortingOrder}
            />
            <HeaderButton
              onClick={handleTalentClick}
              icon={FilterIcon}
              title="Talent"
              aria-label="Talent Filter"
              iconOnly={true}
              iconProps={{ boxSize: 4 }}
            />
            {isAudienceFiltersEnabled && (
              <HeaderButton
                onClick={handleAudienceClick}
                icon={FilterIcon}
                title="Audience"
                aria-label="Audience Filters"
                iconOnly={false}
                iconProps={{ boxSize: 4 }}
              />
            )}
            <ToggleView
              defaultValue={activeView}
              setActiveView={setActiveView}
            />
          </Flex>
        </Flex>
        <AppliedAdvancedFilters
          isShared
          filters={appliedFiltersGrouped}
          enabledControls={list?.enabledControls || []}
        />
        {isLoading ? (
          <Spinner centered />
        ) : allFilters.length > 0 && creators.length === 0 ? (
          <TalentRosterEmptyResults />
        ) : (
          creators.length > 0 &&
          (activeView === VIEW_TYPE.stacked ||
          activeView === VIEW_TYPE['card'] ? (
            <Box>
              <Virtuoso
                components={{
                  // @ts-ignore
                  List: ListContainer,
                  ScrollSeekPlaceholder: StackCreatorSkeleton,
                }}
                data={creators}
                increaseViewportBy={300}
                useWindowScroll
                endReached={handleFetchNextPage}
                totalCount={totalItems}
                itemContent={(_index, creator) => {
                  const stackViewCreatorOptionalProps: Partial<IStackViewCreatorProps> =
                    {};

                  stackViewCreatorOptionalProps.note =
                    creator?.notes || undefined;
                  stackViewCreatorOptionalProps.onNoteOpen = () =>
                    onNoteOpen(creator);

                  return (
                    <StackViewCreator
                      list={list}
                      creator={creator}
                      isCheckEnabled={false}
                      isShared
                      key={creator.id}
                      {...stackViewCreatorOptionalProps}
                    />
                  );
                }}
              />
            </Box>
          ) : (
            // @ts-ignore
            <ListView
              toggleOrder={toggleOrder}
              agencyData={creators}
              hasNextPage={hasNextPage}
              isInitialFetch={isInitialFetch}
              isFetchingNextPage={isFetchingNextPage}
              isFieldSorted={isFieldSorted}
              fetchNextPage={fetchNextPage}
              isShared
              isLoading={isLoading}
            />
          ))
        )}
      </Box>
      {/* Sidebars */}
      <TalentFilters />
      <FiltersDrawer />
    </>
  );
}
